import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiAlertTriangle } from 'react-icons/fi';

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const LostProjectsChart = ({ data, loading, error, onDrillDown }) => {
  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading lost projects</p>
    </Card>
  );

  const d = data || {};
  const reasons = d.reason_breakdown || [];
  const maxReasonCount = Math.max(...reasons.map(r => r.count), 1);

  const fmtW = (n) => n > 0 ? `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })} MT` : null;

  const segments = [
    { label: 'Quotation Lost', count: d.quotation_lost_count || 0, value: d.quotation_lost_value || 0, weight: d.quotation_lost_weight || 0, color: '#F59E0B', bg: '#FEF3C7', filterType: 'quotation' },
    { label: 'Job Lost',       count: d.job_lost_count || 0,       value: d.job_lost_value || 0,       weight: d.job_lost_weight || 0,       color: '#EF4444', bg: '#FEE2E2', filterType: 'job' }
  ];

  const handleSegmentClick = (seg) => {
    if (seg.count === 0) return;
    if (!onDrillDown) return;

    const title = `${seg.label} (${seg.count})`;
    const extraParams = { lost_type: seg.filterType };
    onDrillDown(title, extraParams);
  };

  const handleReasonClick = (reason) => {
    if (!onDrillDown) return;
    const title = `Lost Projects - ${reason.reason} (${reason.count})`;
    const extraParams = { lost_reason: reason.reason };
    onDrillDown(title, extraParams);
  };

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-red-50 tw-via-white tw-to-orange-50 tw-shadow-sm tw-h-full">
      <div className="tw-p-5 tw-border-b tw-border-gray-200">
        <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiAlertTriangle className="tw-text-red-500" size={18} />
          Lost Projects
        </h3>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
          Total: <span className="tw-font-semibold tw-text-gray-700">{d.total_lost || 0}</span> lost projects
        </p>
      </div>

      <div className="tw-p-5 tw-space-y-4">
        {/* Quotation vs Job lost */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          {segments.map((seg) => (
            <button
              key={seg.label}
              onClick={() => handleSegmentClick(seg)}
              disabled={seg.count === 0}
              className="tw-rounded-lg tw-p-4 tw-text-left tw-transition-all tw-cursor-pointer disabled:tw-cursor-default hover:tw-shadow-md disabled:tw-opacity-50"
              style={{ backgroundColor: seg.bg }}
            >
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" style={{ color: seg.color }}>{seg.label}</p>
              <p className="tw-text-2xl tw-font-bold tw-mt-1" style={{ color: seg.color }}>{seg.count}</p>
              <p className="tw-text-xs tw-mt-0.5 tw-text-gray-500">{fmtM(seg.value)}</p>
              {fmtW(seg.weight) && (
                <p className="tw-text-xs tw-font-semibold tw-text-cyan-600">{fmtW(seg.weight)}</p>
              )}
            </button>
          ))}
        </div>

        {/* Lost reasons */}
        {reasons.length > 0 && (
          <div>
            <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2.5">Top Reasons</p>
            <div className="tw-space-y-2">
              {reasons.map((r, i) => {
                const pct = (r.count / maxReasonCount) * 100;
                const color = i < 3 ? '#EF4444' : '#F87171';
                return (
                  <button
                    key={i}
                    onClick={() => handleReasonClick(r)}
                    className="tw-w-full tw-flex tw-items-center tw-gap-2.5 tw-px-2 tw-py-1 tw-rounded hover:tw-bg-gray-100 tw-transition-colors tw-text-left"
                  >
                    <span className="tw-text-xs tw-text-gray-600 tw-flex-1 tw-truncate">{r.reason || '—'}</span>
                    <div className="tw-w-20 tw-h-1.5 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                      <div className="tw-h-full tw-rounded-xl" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                    <span className="tw-text-xs tw-font-bold tw-text-gray-700 tw-w-5 tw-text-right">{r.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

LostProjectsChart.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.any,
  onDrillDown: PropTypes.func
};
LostProjectsChart.defaultProps = {
  data: null,
  loading: false,
  error: null,
  onDrillDown: null
};

export default LostProjectsChart;
